import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Text } from '@/components/atoms';

interface ScrollingNumberProps {
  value: number;
  prefix?: string | React.ReactNode;
  suffix?: string | React.ReactNode;
  className?: string;
}

const ScrollingNumber: React.FC<ScrollingNumberProps> = ({
  value,
  prefix,
  suffix,
  className = '',
}) => {
  const [currentValue, setCurrentValue] = useState<string[]>([]);
  const prevValueRef = useRef<string[]>([]);
  const currentValueRef = useRef<string[]>([]);

  // Format the value to fixed 2 decimal points, ensuring at least 2 digits before the decimal
  const formatValue = (num: number) => {
    return num
      .toLocaleString('en-US', {
        minimumIntegerDigits: 2,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: false,
      })
      .split('');
  };

  useEffect(() => {
    const formattedValue = formatValue(value);
    if (prevValueRef.current.length === 0) {
      prevValueRef.current = formattedValue;
      setCurrentValue(formattedValue);
    } else {
      prevValueRef.current = currentValueRef.current;
      setCurrentValue(formattedValue);
    }
    currentValueRef.current = formattedValue;
  }, [value]);

  const digitVariants = {
    enter: (direction: number) => ({
      y: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      y: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      y: direction < 0 ? '100%' : '-100%',
      opacity: 0,
    }),
  };

  return (
    <div className={`inline-flex items-center gap-2xs ${className}`}>
      {prefix && (
        <div>{typeof prefix === 'string' ? <Text>{prefix}</Text> : prefix}</div>
      )}
      <div className="inline-flex overflow-hidden">
        {currentValue.map((digit, index) => {
          const prevDigit = prevValueRef.current[index];
          const direction = parseInt(digit) - parseInt(prevDigit);
          return (
            <div
              key={index}
              className="relative h-md w-xs flex flex-col items-center justify-center overflow-hidden"
            >
              <AnimatePresence initial={false} custom={direction}>
                <motion.div
                  key={digit}
                  custom={direction}
                  variants={digitVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    y: { type: 'spring', stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 },
                  }}
                  className="absolute"
                >
                  <Text color={'primary'} size={'body'}>
                    {digit}
                  </Text>
                </motion.div>
              </AnimatePresence>
            </div>
          );
        })}
      </div>
      {suffix && (
        <div>
          {typeof suffix === 'string' ? (
            <Text color={'secondary'} size={'overline'}>
              {suffix}
            </Text>
          ) : (
            suffix
          )}
        </div>
      )}
    </div>
  );
};

export default ScrollingNumber;
